import * as Sentry from "@sentry/react"
import { debounce } from "lodash-es"
import { computed, observable, reaction, runInAction } from "mobx"
import { toast } from "sonner"

import { createClient, DevServer } from "@/iframe/lib/proto.ts"
import { formatDateTime } from "@/lib/date.ts"
import { getLogger } from "@/lib/logging.ts"
import { Model } from "@/lib/Model.ts"
import { renameFileIfNeeded, successfulAttachments } from "@/lib/std.ts"
import { removeTokenParams } from "@/lib/url.ts"
import {
  PREVIEW_DEVICE_MODE_STORAGE_KEY,
  PREVIEW_DEVICE_TYPE_STORAGE_KEY,
  PREVIEW_HOT_RELOAD_STORAGE_KEY,
  PREVIEW_IGNORED_ERRORS_STORAGE_KEY,
  PREVIEW_ZOOM_STORAGE_KEY
} from "@/routes/tasks/TaskViewScene/persistence.ts"
import { PreviewErrorsToast } from "@/routes/tasks/TaskViewScene/PreviewErrorsToast.tsx"

import type { PromptInputModel } from "@/components/PromptInput/PromptInputModel.ts"
import type { BrowserRpcClientApi, PreviewEvents, PreviewMethods } from "@/iframe/lib/proto.ts"
import type { PR_Project2 } from "@/lib/client/generated/TypeDefinitions.ts"
import type { Persistence } from "@/lib/persistence.ts"
import type { IComputedValue } from "mobx"

export type DeviceType = "mobile" | "tablet" | "desktop"

export interface DevicePreset {
  name: string
  type: DeviceType
  width: number
  height: number
  userAgent?: string
}

export const DEVICE_PRESETS: DevicePreset[] = [
  { name: "iPhone 17", type: "mobile", width: 402, height: 874 },
  { name: "Pixel 9", type: "mobile", width: 412, height: 915 },
  { name: "Samsung Galaxy S25", type: "mobile", width: 384, height: 854 },
  { name: "iPad Pro 12.9″", type: "tablet", width: 1024, height: 1366 },
  { name: "iPad Air", type: "tablet", width: 820, height: 1180 },
]

const log = getLogger("TaskPreviewTabModel")

export class TaskPreviewTabModel extends Model {
  toastId: string | number | undefined
  readonly client: BrowserRpcClientApi<PreviewMethods, PreviewEvents>

  constructor(
    readonly taskId: string,
    readonly project: PR_Project2,
    readonly initialUrl: string,
    readonly inputModel: PromptInputModel,
    readonly currentVersionCommitId: IComputedValue<string | null>,
    readonly persistence: Persistence,
    readonly devMode: boolean
  ) {
    super("TaskPreviewTabModel")

    // Measure preview loading time and report it to Sentry
    this.lifetime.add(reaction(
      () => ({
        loaded: this.loaded.get(),
      }),
      ({ loaded }) => {
        if (loaded) {
          this.loadingSpan?.end()
        } else {
          this.loadingSpan = Sentry.startInactiveSpan({
            forceTransaction: true,
            name: "loading_time",
            op: "preview.load"
          })

          this.loadingSpan.setAttribute("task.id", taskId)
          this.loadingSpan.setAttribute("task.project", project.key.key)
          this.loadingSpan.setAttribute("preview.url", initialUrl)
        }
      },
      { fireImmediately: true }
    ), "TaskPreviewTabModel:loaded")

    // In the case of a known dev server (Vite, Webpack), we rely on the iframe to reload itself when the version changes.
    // Otherwise, we need to reload the iframe manually.

    // hotReload and devServer observables are intentionally not included in reaction dependencies.
    this.lifetime.add(reaction(
      () => ({
        currentVersionCommitId: this.currentVersionCommitId.get(),
      }),
      () => {
        if (!this.hotReload.get() || this.devServer.get() === DevServer.Unknown) {
          void iframeClient.reload()
        }
      }
    ), "TaskPreviewTabModel:devServer")

    // Display errors in a toast
    this.lifetime.add(reaction(
      () => ({
        caughtErrors: this.caughtErrors.get(),
      }),
      ({ caughtErrors }) => {
        this.updateErrorToast(Array.from(caughtErrors.values()))
      }
    ), "TaskPreviewTabModel:caughtErrors")

    // Clear errors when the version changes
    this.lifetime.add(reaction(
      () => ({
        currentVersionCommitId: this.currentVersionCommitId.get(),
      }),
      ({ currentVersionCommitId }, prev) => {
        if (currentVersionCommitId !== null && prev.currentVersionCommitId !== null && currentVersionCommitId !== prev.currentVersionCommitId) {
          if (this.caughtErrors.get().size > 0) {
            this.caughtErrors.set(new Map())
            if (this.toastId != null) {
              toast.dismiss(this.toastId)
            }
          }
        }
      }
    ), "TaskPreviewTabModel:currentVersionCommitId")

    this.currentUrl.set(initialUrl)

    const savedZoom = parseFloat(this.persistence.getItem(PREVIEW_ZOOM_STORAGE_KEY) ?? "0.5")
    this.zoom.set(savedZoom)

    const savedDeviceMode = this.persistence.getItem(PREVIEW_DEVICE_MODE_STORAGE_KEY) === "true"
    this.deviceMode.set(savedDeviceMode)

    const savedDeviceType = this.persistence.getItem(PREVIEW_DEVICE_TYPE_STORAGE_KEY)
    if (savedDeviceType) {
      const preset = DEVICE_PRESETS.find(p => p.name === savedDeviceType)
      if (preset) {
        this.devicePreset.set(preset)
      }
    }

    this.lifetime.add(reaction(
      () => ({
        zoom: this.zoom.get()
      }),
      ({ zoom }) => {
        this.persistence.setItem(PREVIEW_ZOOM_STORAGE_KEY, zoom.toString())
      }
    ), "TaskPreviewTabModel:zoom")

    this.lifetime.add(reaction(
      () => ({
        deviceMode: this.deviceMode.get(),
        devicePreset: this.devicePreset.get()
      }),
      ({ deviceMode, devicePreset }) => {
        this.persistence.setItem(PREVIEW_DEVICE_MODE_STORAGE_KEY, deviceMode.toString())
        this.persistence.setItem(PREVIEW_DEVICE_TYPE_STORAGE_KEY, devicePreset.name)
      }
    ), "TaskPreviewTabModel:deviceMode")

    const iframeClient = this.client = createClient<PreviewMethods, PreviewEvents>(() => {
      return this.iframeRef?.contentWindow || null
    })

    iframeClient.on("elementSelected", (data) => {
      runInAction(() => {
        this.pickerActive.set(false)

        const elements = this.inputModel.elements.get().slice().concat({
          id: Math.random().toString(36).substring(2),
          name: data.name,
          path: data.path,
          screenshotPngBase64: data.screenshotPngBase64
        })

        this.inputModel.elements.set(elements)

        this.inputModel.highlight()
      })
    })

    iframeClient.on("locationChanged", (data) => {
      runInAction(() => this.currentUrl.set(data.url))
    })

    iframeClient.on("pickerDismissed", () => {
      runInAction(() => this.pickerActive.set(false))
    })

    iframeClient.on("errorOccurred", (data) => {
      const error = new Error(data.error.message)
      error.stack = data.error.stack

      runInAction(() => {
        this.caughtErrors.set(new Map(this.caughtErrors.get()).set(error.stack ?? "No stack trace available", error))
      })
    })

    iframeClient.on("devServerDetected", (data) => {
      runInAction(() => {
        this.devServer.set(data.kind)
        const storageKey = `${this.project.key.key}/${PREVIEW_HOT_RELOAD_STORAGE_KEY}`
        this.hotReload.set((data.kind === DevServer.Vite || data.kind === DevServer.Webpack) && this.persistence.getItem(storageKey) !== "false")
      })

      this.maybeClearDevServerDetectionTimeout()
    })

    iframeClient.on("unload", () => {
      runInAction(() => this.loaded.set(false))
    })

    this.lifetime.add(iframeClient.dispose, "TaskPreviewTabModel:iframeClient")

    this.displayUrl = computed(() => {
      return removeTokenParams(this.currentUrl.get())
    })
  }

  private updateErrorToast = debounce((caughtErrors: Error[]) => {
    const storageKey = `${PREVIEW_IGNORED_ERRORS_STORAGE_KEY}/${this.taskId}`

    // Remember pre-existing errors to avoid bothering the user with them
    if (this.currentVersionCommitId.get() == null) {
      this.persistence.setItem(storageKey, JSON.stringify(caughtErrors.map(e => e.stack)))
      return
    }

    let errorsToDisplay: Error[] = [...caughtErrors]

    if (caughtErrors.length > 0) {
      const ignoredErrors = this.persistence.getItem(storageKey)

      if (ignoredErrors != null) {
        try {
          const ignoredErrorStacks = JSON.parse(ignoredErrors)

          if (Array.isArray(ignoredErrorStacks)) {
            errorsToDisplay = errorsToDisplay.filter(error => !ignoredErrorStacks.includes(error.stack))
          }
        } catch {
          this.persistence.removeItem(storageKey)
        }
      }
    }

    if (errorsToDisplay.length > 0 && this.devMode) {
      if (this.toastId != null) {
        toast.dismiss(this.toastId)
      }
      this.toastId = toast.error(<PreviewErrorsToast errors={errorsToDisplay} />, {
        id: "task-preview-errors",
        duration: Infinity
      })

      this.lifetime.add(() => toast.dismiss(this.toastId), "TaskPreviewTabModel:toast")
    }
  }, 300)

  readonly currentUrl = observable.box<string>("")
  readonly displayUrl: IComputedValue<string>

  readonly zoom = observable.box(0.5)

  readonly caughtErrors = observable.box(new Map<string, Error>())

  readonly pickerActive = observable.box(false)

  // Toggle on each load/unload
  readonly loaded = observable.box(false)

  // Used to measure loading time
  private loadingSpan: ReturnType<typeof Sentry.startInactiveSpan> | null = null

  // Set once the first load is complete
  readonly loadedFirst = observable.box(false)

  // Set once dev server scripts are detected in an iframe, meaning that we don't need to reload the iframe when there's a new version
  readonly devServer = observable.box<DevServer | undefined>()

  // Hot reload toggle - enabled by default for known dev servers
  readonly hotReload = observable.box(false)

  readonly deviceMode = observable.box(false)
  readonly devicePreset = observable.box<DevicePreset>(DEVICE_PRESETS[0])

  iframeRef: HTMLIFrameElement | null = null

  readonly screenshotInProgress = observable.box(false)

  private devServerDetectionTimeoutId: number | null = null

  readonly onIframeLoad = () => {
    runInAction(() => {
      this.loaded.set(true)
      this.loadedFirst.set(true)
    })

    this.maybeClearDevServerDetectionTimeout()

    if (this.devServer.get() === undefined) {
      this.devServerDetectionTimeoutId = window.setTimeout(() => {
        toast.info("Preview couldn't be loaded")
        this.devServerDetectionTimeoutId = null
      }, 5000)
    }
  }

  readonly onIframeError = (event: ErrorEvent) => {
    log.error("Preview iframe error:", event.error)
  }

  readonly takeScreenshot = () => {
    runInAction(() => this.screenshotInProgress.set(true))

    this.takeScreenshotAndUpdateContext()
      .catch(err => {
        toast.error("Failed to take screenshot: " + err.message)
        log.error("Failed to take screenshot:", err)
      })
      .finally(() => runInAction(() => this.screenshotInProgress.set(false)))
  }

  readonly toggleDeviceMode = () => {
    runInAction(() => {
      this.deviceMode.set(!this.deviceMode.get())
    })
  }

  readonly setDevicePreset = (preset: DevicePreset) => {
    runInAction(() => {
      this.devicePreset.set(preset)
    })
  }

  readonly toggleHotReload = () => {
    runInAction(() => {
      const value = !this.hotReload.get()
      this.hotReload.set(value)
      const storageKey = `${this.project.key.key}/${PREVIEW_HOT_RELOAD_STORAGE_KEY}`
      this.persistence.setItem(storageKey, value.toString())
    })
  }

  readonly togglePicker = () => {
    const nextState = !this.pickerActive.get()
    runInAction(() => {
      this.pickerActive.set(nextState)
    })

    this.client.togglePicker(nextState).catch((e) => log.error("Failed to toggle picker", e))
  }

  private async takeScreenshotAndUpdateContext() {
    const screenshot = await this.client.takeScreenshot()

    const response = await fetch(screenshot)

    if (!response.ok) {
      const err = `Failed to take screenshot: ${response.statusText}`
      toast.error(err)
      log.error(err)
    }

    const blob = await response.blob()

    const timestamp = formatDateTime(Date.now(), true, true)
    const fileName = `Screenshot ${timestamp}.png`
    const file = new File([blob], fileName, { type: "image/png" })

    const filenames = (await successfulAttachments(this.inputModel.attachments.get())).map(it => it.filename)

    runInAction(() => {
      const uploadPromise = this.inputModel.onUploadAttachment(renameFileIfNeeded(file, filenames))
      if (uploadPromise !== null) {
        this.inputModel.attachments.set([
          ...this.inputModel.attachments.get(),
          uploadPromise
        ])
      }
    })

    this.inputModel.highlight()
  }

  maybeClearDevServerDetectionTimeout() {
    if (this.devServerDetectionTimeoutId !== null) {
      clearTimeout(this.devServerDetectionTimeoutId)
      this.devServerDetectionTimeoutId = null
    }
  }

  terminate() {
    super.terminate()
    this.maybeClearDevServerDetectionTimeout()
    toast.dismiss(this.toastId)
    this.client.dispose()
  }
}